// src/pages/FindCar.jsx
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useFilters } from "../stores/useFilters";
import { useAuth } from "../context/AuthContext";
import api from "../api/axios";
import {
  getMakes,
  getModels,
  getBodyTypes,
  getFuelTypes,
  getVehicles,
} from "../api/cars";
import PageImageSelector from "../components/ui/PageImageSelector";
import LoadingSpinner from "../components/LoadingSpinner";
import whyZorqkImg from "../assets/findcar/whyzorqk.png";
import { BiEdit } from "react-icons/bi";
import carsImage from "../assets/findcar/Main image.png";
import FaqSection from "../components/FaqSection";
// import TestimonialSection from "../components/home/TestimonialsSection";

// icons
import wz1 from "../assets/findcar/wz1.png";
import wz2 from "../assets/findcar/wz2.png";
import wz3 from "../assets/findcar/wz3.png";
import guaranteeIcon from "../assets/findcar/guarantee_icon.png";
import phoneIcon from "../assets/findcar/phone_icon.png";
import ratingIcon from "../assets/findcar/rating_icon.png";
import BrandBrowseSection from "../components/home/BrandBrowseSection";
import { BODY_TYPES } from "../data/bodyTypes";
import { mapBodyTypeToShape } from "../lib/mapBodyTypes";


export default function FindCar() {
  const navigate = useNavigate();
  const { set, reset } = useFilters();
  const { user } = useAuth();
  const isAdmin = user?.roles.includes("super-admin");
  const qc = useQueryClient();
  const whyFileInputRef = useRef();

  // ─── Search form state ────────────────────────────────────────
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [priceType, setPriceType] = useState("monthly"); // "monthly" or "total"
  const [maxMonthlyBudget, setMaxMonthlyBudget] = useState("");

  // Budget options
  const monthlyBudgetOptions = Array.from({ length: 19 }, (_, i) => {
    const value = 50 * (i + 1);
    return { value: value.toString(), label: `£${value}` };
  });

  // Total price options: 5,500 to 50,000 in increments of 500
  const totalPriceOptions = Array.from({ length: ((50000 - 5500) / 500) + 1 }, (_, i) => {
    const value = 5500 + 500 * i;
    return { value: value.toString(), label: `£${value.toLocaleString()}` };
  });

  // ─── CMS page content ────────────────────────────────────────
  const {
    data: fetchedPage,
    isLoading: pageLoading,
    isError: pageError,
    error,
  } = useQuery({
    queryKey: ["page", "find-car"],
    queryFn: async () => {
      const res = await api.get("/pages/find-car");
      return res.data.content ?? res.data;
    },
    staleTime: 300_000,
  });

  // local copy
  const [pageContent, setPageContent] = useState(null);
  useEffect(() => {
    if (fetchedPage) setPageContent(fetchedPage);
  }, [fetchedPage]);

  // ─── Global edit‐mode for any section ─────────────────────────
  const [editingSection, setEditingSection] = useState(null);

  // ─── Save mutation ────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: (updatedContent) =>
      api.put("/pages/find-car", { content: updatedContent }),
    onSuccess: (_, updatedContent) => {
      setPageContent(updatedContent);
      setEditingSection(null);
      toast.success("Saved successfully");
      qc.invalidateQueries({ queryKey: ["page", "find-car"] });
    },
    onError: () => toast.error("Failed to save"),
  });

  // ─── Helper to update any section by key ──────────────────────
  function updateSection(key, updater) {
    setPageContent((prev) => ({
      ...prev,
      sections: prev.sections.map((sec) =>
        sec.key === key ? updater(sec) : sec
      ),
    }));
  }

  // ─── Fetch dropdowns & counts ─────────────────────────────────
  const { data: makes = [] } = useQuery({
    queryKey: ["landing", "makes"],
    queryFn: getMakes,
    staleTime: 300_000,
  });
  const { data: models = [] } = useQuery({
    queryKey: ["landing", "models", make],
    queryFn: () => getModels(make),
    enabled: Boolean(make),
    staleTime: 300_000,
  });
  const { data: rawBodyTypes = [] } = useQuery({
    queryKey: ["landing", "bodyTypes"],
    queryFn: getBodyTypes,
    staleTime: 300_000,
  });
  const { data: rawFuelTypes = [] } = useQuery({
    queryKey: ["landing", "fuelTypes"],
    queryFn: getFuelTypes,
    staleTime: 300_000,
  });

  // Fetch total cars count
  const { data: totalCarsData } = useQuery({
    queryKey: ["landing", "totalCars"],
    queryFn: async () => {
      const response = await getVehicles({ page: 1, limit: 1 });
      return response;
    },
    staleTime: 300_000,
  });

  const totalCars = totalCarsData?.total || 0;

  // Count cars whose fuel type is ELECTRIC or ELECTRICITY (case-insensitive)
  const electricCount = React.useMemo(() => {
    const targets = new Set(["ELECTRIC", "ELECTRICITY"]);
    return (rawFuelTypes || []).reduce((sum, item) => {
      const key = (item?.value || "").trim().toUpperCase();
      const n = Number(item?.count) || 0;
      return sum + (targets.has(key) ? n : 0);
    }, 0);
  }, [rawFuelTypes]);

  const bodyTypeMap = React.useMemo(() => {
    const m = {};

    (rawBodyTypes || []).forEach(({ value: bodyType, count, fuelType }) => {
      const mappedShape = mapBodyTypeToShape(bodyType, fuelType);
      m[mappedShape] = (m[mappedShape] || 0) + (Number(count) || 0);
    });

    // Inject ELECTRIC card count (from fuel types, not body types)
    m.ELECTRIC = electricCount;

    return m;
  }, [rawBodyTypes, electricCount]);

  const shapeToBackendValues = React.useMemo(() => {
    const map = {};

    rawBodyTypes.forEach(({ value: bodyType, fuelType }) => {
      const shape = mapBodyTypeToShape(bodyType, fuelType);
      if (!shape) return;

      // Skip mapping to bodyType for ELECTRIC – it's handled via fuelType
      if (shape === "ELECTRIC") return;

      if (!map[shape]) map[shape] = new Set();
      map[shape].add(shape); // canonical
      map[shape].add(bodyType.toUpperCase().trim()); // original raw
    });

    // Ensure other shapes exist at least with their canonical key
    BODY_TYPES.forEach(({ key }) => {
      if (key === "ELECTRIC") return; // keep ELECTRIC out of bodyType mapping
      if (!map[key]) map[key] = new Set([key]);
    });

    const out = {};
    Object.keys(map).forEach((k) => (out[k] = Array.from(map[k])));
    return out;
  }, [rawBodyTypes]);

  // ─── Handlers ────────────────────────────────────────────────

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    reset();
    set("make", make);
    set("model", model);
    set("priceType", priceType); // Add to filter store
    set("maxMonthlyBudget", maxMonthlyBudget); // Add to filter store
    navigate("/used-cars");
  };

  const handleShapeClick = (typeKey) => {
    reset();

    if (typeKey === "ELECTRIC") {
      // Only fuel type, no bodyType
      set("fuelType", ["ELECTRIC", "ELECTRICITY"]);
      // Optional: explicitly clear bodyType if your store keeps old values
      set("bodyType", []);
    } else {
      const backendValues = shapeToBackendValues[typeKey] ?? [typeKey];
      set("bodyType", backendValues);
      // Optional: clear fuelType so it doesn't linger from a previous search
      set("fuelType", []);
    }

    navigate("/used-cars");
  };

  if (pageLoading || !pageContent) return <LoadingSpinner />;
  if (pageError) return <p className="text-red-600">Error: {error.message}</p>;

  const { hero, sections } = pageContent;
  const whySection = sections.find((s) => s.key === "why-zorqk") || {
    heading: "",
    items: [],
  };

  // Helper to upload a single page image under “about-us” prefix
  async function uploadPageImage(file, onUrlReceived) {
    const form = new FormData();
    form.append("pageKey", "find-car");
    form.append("images", file);

    try {
      const res = await api.post("/page-images", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newImg = res.data[0];
      onUrlReceived(newImg.url);
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error("Image upload failed");
    }
  }

  const sortedBodies = [
    ...BODY_TYPES.filter((body) => (bodyTypeMap[body.key] || 0) > 0),
    ...BODY_TYPES.filter((body) => (bodyTypeMap[body.key] || 0) === 0),
  ];

  return (
    <div className="bg-accent">
      {/* HERO SECTION  */}
      <section
        className="relative text-white"
        style={{
          background: "linear-gradient(#1F2954 10%, #EF514B 77%)",
        }}
      >
        <div className="w-full mx-auto pt-24 flex flex-col md:flex-row items-center justify-between">
          {/* Text content */}
          <div className="w-full md:w-1/2 flex flex-col justify-center items-center mb-10 md:mb-0 px-5 md:px-0">
            <h1 className="text-xl md:text-2xl font-bold mb-6">
              Find your dream car
            </h1>
            <div className="bg-white p-10 rounded-3xl shadow-xl text-black space-y-8 max-w-md w-full">
              <h2 className="text-xl md:text-2xl font-bold text-center text-primary">
                Buy used cars online
              </h2>

              <form onSubmit={handleSearchSubmit} className="space-y-6">
                {/* ─── Make dropdown ────────────────────────────── */}
                <div className="relative">
                  <select
                    value={make}
                    onChange={(e) => setMake(e.target.value)}
                    className="appearance-none w-full border border-[#10162F] rounded-full px-6 py-3  focus:outline-none"
                  >
                    <option value="" disabled hidden>
                      Select make
                    </option>
                    {makes.map(({ value, count }) => (
                      <option key={value} value={value}>
                        {value}
                        {count > 0 && ` (${count})`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* ─── Model dropdown ───────────────────────────── */}
                <div className="relative">
                  <select
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    disabled={!make || models.length === 0}
                    className="appearance-none w-full border border-[#10162F] rounded-full px-6 py-3 focus:outline-none disabled:opacity-50"
                  >
                    <option value="" disabled hidden>
                      Select model
                    </option>
                    {models.map(({ value, count }) => (
                      <option key={value} value={value}>
                        {value}
                        {count > 0 && ` (${count})`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* ─── Price Type and Budget Filters ────────────── */}
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-8 justify-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="priceType"
                        value="monthly"
                        checked={priceType === "monthly"}
                        onChange={() => setPriceType("monthly")}
                        className="accent-[#6C3DF4]"
                      />
                      <span className="font-medium">Monthly price</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="priceType"
                        value="total"
                        checked={priceType === "total"}
                        onChange={() => setPriceType("total")}
                        className="accent-[#6C3DF4]"
                      />
                      <span className="font-medium">Total price</span>
                    </label>
                  </div>
                  <div className="relative">
                    <select
                      value={maxMonthlyBudget}
                      onChange={(e) => setMaxMonthlyBudget(e.target.value)}
                      className="appearance-none w-full border border-[#10162F] rounded-full px-6 py-3 focus:outline-none"
                    >
                      <option value="" disabled hidden>
                        {priceType === "total" ? "Max total price" : "Max monthly budget"}
                      </option>
                      {(priceType === "total" ? totalPriceOptions : monthlyBudgetOptions).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* ─── Search Button ───────────────────────────── */}
                <button
                  type="submit"
                  className="w-full bg-[#EF4A3C] text-white py-3 rounded-full font-semibold"
                >
                  Search {totalCars.toLocaleString()} cars
                </button>
              </form>

              <h2 className="text-base md:text-lg font-bold text-center text-primary">
                All our cars are VAT qualifying
              </h2>
              <p className="md:hidden text-center text-primary text-sm mt-1">
                📞 Call us ·{" "}
                <a href="tel:03337723333" className="underline hover:text-accent">
                  0333 772 3333
                </a>
              </p>
            </div>
          </div>

          {/* Cars image */}
          <div className="w-full md:w-1/2 hidden md:block">
            <img
              src={carsImage}
              alt="Car Lineup"
              className="w-full object-contain"
            />
          </div>
        </div>
      </section>

      {/* ─── SHOP BY SHAPE ───────────────────────────────────────── */}
      <div className="bg-primary rounded-t-[30px] py-10 w-full mx-auto px-4 md:px-12 mt-5">
        {/* icons  */}
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row justify-between items-center text-center gap-10">
          {/* Icon Block: Guarantee */}
          <div className="flex flex-col gap-5 justify-center items-center">
            <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center shadow text-white">
              <img
                src={guaranteeIcon}
                alt="14 day guarantee"
                className="w-8 h-8 object-contain"
              />
            </div>
            <p className="font-bold text-base md:text-lg text-center">
              14 day guarantee**
            </p>
          </div>

          {/* Icon Block: Get the app */}
          <div className="flex flex-col gap-5 justify-center items-center">
            <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center shadow text-white">
              <img
                src={phoneIcon}
                alt="phone"
                className="w-8 h-8 object-contain"
              />
            </div>
            <p className="font-bold text-base md:text-lg text-center">
              Get the app
            </p>
          </div>

          {/* Icon Block: Reviews */}
          <div className="flex flex-col gap-5 justify-center items-center">
            <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center shadow text-white">
              <img
                src={ratingIcon}
                alt="5 star reviews"
                className="w-8 h-8 object-contain"
              />
            </div>
            <p className="font-bold text-base md:text-lg text-center">
              5* Reviews
            </p>
          </div>
        </div>

        <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-white mt-16 mb-5 text-center">
          Shop by shape
        </h2>

        {/* Body Type Cards */}
        <div className="flex justify-center">
          <div className="inline-grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {sortedBodies
              .filter((body) => (bodyTypeMap[body.key] || 0) > 0)
              .map((body) => {
                const total = bodyTypeMap[body.key] || 0;
                return (
                  <div
                    key={body.key}
                    className="
              bg-white rounded-3xl shadow-xl 
              hover:shadow-2xl transform hover:-translate-y-1 hover:scale-[1.03]
              transition duration-300 ease-in-out
              p-6 flex flex-col items-center text-center
            "
                  >
                    <h3 className="text-brandAccent text-xl font-bold mb-1">
                      {body.label}
                    </h3>

                    <img
                      src={body.img}
                      alt={body.label}
                      className="w-full h-32 object-contain mb-4"
                    />

                    <p className="text-secondary mb-4">{body.seats}</p>

                    <button
                      onClick={() => handleShapeClick(body.key)}
                      className="
                mt-auto inline-flex items-center justify-center
                px-8 py-2 border border-accent font-bold text-brandAccent
                rounded-full transition
                hover:bg-indigo-50
              "
                    >
                      Shop {total} {total === 1 ? "car" : "cars"}
                    </button>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      <BrandBrowseSection makes={makes} />

      {/* ─── WHY ZORQK? ─────────────────────────────────────────── */}
      <div className="w-full max-w-5xl mx-auto px-4 pt-10 relative">
        {isAdmin && editingSection !== "why-zorqk" && (
          <BiEdit
            onClick={() => setEditingSection("why-zorqk")}
            className="absolute top-4 right-4 text-2xl text-brandAccent cursor-pointer hover:text-accent"
          />
        )}

        {editingSection === "why-zorqk" ? (
          <div className="space-y-4">
            <input
              type="text"
              value={whySection.heading}
              className="text-primary w-full mb-4 px-3 py-2 border rounded"
              onChange={(e) =>
                updateSection("why-zorqk", (sec) => ({
                  ...sec,
                  heading: e.target.value,
                }))
              }
            />

            {whySection.items.map((item, idx) => (
              <div key={idx} className="border p-4 rounded space-y-2">
                <select
                  value={item.icon}
                  onChange={(e) =>
                    updateSection("why-zorqk", (sec) => ({
                      ...sec,
                      items: sec.items.map((it, j) =>
                        j === idx ? { ...it, icon: e.target.value } : it
                      ),
                    }))
                  }
                  className="text-primary py-1 border rounded"
                >
                  <option value="car">Car</option>
                  <option value="calculator">Calculator</option>
                  <option value="truck">Truck</option>
                </select>
                <input
                  type="text"
                  value={item.title}
                  className="text-primary w-full px-2 py-1 border rounded"
                  onChange={(e) =>
                    updateSection("why-zorqk", (sec) => ({
                      ...sec,
                      items: sec.items.map((it, j) =>
                        j === idx ? { ...it, title: e.target.value } : it
                      ),
                    }))
                  }
                />
                <textarea
                  rows={2}
                  value={item.description}
                  className="text-primary w-full px-2 py-1 border rounded"
                  onChange={(e) =>
                    updateSection("why-zorqk", (sec) => ({
                      ...sec,
                      items: sec.items.map((it, j) =>
                        j === idx ? { ...it, description: e.target.value } : it
                      ),
                    }))
                  }
                />
              </div>
            ))}

            {/* ─── Section image chooser ─────────────────────── */}
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-primary">
                Section Image
              </label>
              <PageImageSelector
                pageKey="find-car"
                value={whySection.imageUrl || ""}
                onChange={(newUrl) => {
                  if (newUrl === "__upload_new__") {
                    whyFileInputRef.current.click();
                  } else {
                    updateSection("why-zorqk", (sec) => ({
                      ...sec,
                      imageUrl: newUrl,
                    }));
                  }
                }}
              />
              <input
                type="file"
                accept="image/*"
                ref={whyFileInputRef}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  uploadPageImage(file, (url) => {
                    updateSection("why-zorqk", (sec) => ({
                      ...sec,
                      imageUrl: url,
                    }));
                  });
                  e.target.value = null;
                }}
              />
            </div>

            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => saveMutation.mutate(pageContent)}
                className="px-4 py-2 bg-green-600 text-white rounded"
              >
                Save
              </button>
              <button
                onClick={() => setEditingSection(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <h2 className="text-2xl sm:text-3xl md:text-4xl pl-5 font-semibold text-white mb-10 md:-mb-10 text-center md:text-left ">
              {whySection.heading}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 items-center ml-5 md:ml-0">
              <div className="space-y-10">
                <div className="flex items-center space-x-10">
                  <div className="p-4 bg-primary rounded-full shadow text-white">
                    <img src={wz1} alt="car" className="text-2xl md:text-5xl" />
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-semibold text-white">
                      Great value, high-quality cars
                    </h3>
                  </div>
                </div>
                <div className="flex items-center space-x-10">
                  <div className="p-4 bg-primary rounded-full shadow text-white">
                    <img
                      src={wz2}
                      alt="calculator"
                      className="text-3xl md:text-6xl"
                    />
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-semibold text-white">
                      Choose how you pay
                    </h3>
                  </div>
                </div>
                <div className="flex items-center space-x-10">
                  <div className="p-4 bg-primary rounded-full shadow text-white">
                    <img
                      src={wz3}
                      alt="delivery"
                      className="text-2xl md:text-5xl"
                    />
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-semibold text-white">
                      Delivery or collection
                    </h3>
                  </div>
                </div>
              </div>

              <div className="relative flex justify-center mt-8 md:mt-0">
                <div className="absolute left-1/2 top-1/2 w-80 md:w-96 h-80 md:h-96 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/50" />
                <div className="absolute left-1/2 top-1/2 w-56 md:w-72 h-56 md:h-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary" />
                <img
                  src={whySection.imageUrl || whyZorqkImg}
                  alt="Why Zorqk"
                  className="relative w-full max-w-sm object-cover mb-[90px] mr-12"
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* <TestimonialSection /> */}
      <FaqSection />
    </div>
  );
}
